import { useQueryClient } from "@tanstack/react-query";
import { Building, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@krea/shared/design-system/Dropdown";
import {
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@krea/shared/design-system/sidebar";

import { countryCodeToName } from "@/features/common/constants";
import { useCurrentLenderCompanyStore } from "@/features/lender-companies/current-lender-company-store";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { useGetMyLenderCompaniesQuery } from "@/features/lender-companies/hooks/use-get-my-lender-companies.query";
import { useGetMeQuery } from "@/features/users/hooks/use-get-me.query";

export const AppSidebarHeader = () => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { data: me } = useGetMeQuery();
  const { data: lenderCompaniesData } = useGetMyLenderCompaniesQuery(
    {
      limit: 10,
      offset: 0,
    },
    {
      enabled: !me?.isAdmin,
    },
  );
  const lenderCompanies =
    lenderCompaniesData?.pages.flatMap((page) => page.items) ?? [];
  const { isMobile } = useSidebar();
  const queryClient = useQueryClient();
  const setCurrentLenderCompany = useCurrentLenderCompanyStore(
    (state) => state.setCurrentLenderCompany,
  );
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const onSelectLenderCompany = (companyId: number) => {
    setCurrentLenderCompany(companyId);
    queryClient.resetQueries();
    navigate(`/lender-company/${companyId}`);
  };

  const Wrapper = !me?.isAdmin ? DropdownMenuTrigger : "div";

  return (
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <Wrapper asChild>
              <SidebarMenuButton
                size="lg"
                onClick={(e) => {
                  if (me?.isAdmin) {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/select-lender-company`);
                  }
                }}
              >
                {lenderCompany?.imageUrl ? (
                  <img
                    src={lenderCompany.imageUrl}
                    alt="Company Logo"
                    className="h-8 w-8 rounded-lg object-contain"
                  />
                ) : (
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-white">
                    <Building className="size-4" />
                  </div>
                )}

                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {lenderCompany?.name}
                  </span>
                  {lenderCompany?.countryCode && (
                    <span className="truncate text-xs">
                      {countryCodeToName[lenderCompany.countryCode]}
                    </span>
                  )}
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </Wrapper>

            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-black-100">
                Lender Companies
              </DropdownMenuLabel>
              {lenderCompanies.map((company) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => onSelectLenderCompany(company.id)}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-md border">
                    <Building className="size-3.5 shrink-0" />
                  </div>
                  {company.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  );
};
